import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription: ReturnType<typeof supabase.auth.onAuthStateChange>["data"]["subscription"] | null = null;

    const initializeAuth = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (!mounted) return;

      if (error) {
        console.warn("[auth] failed to restore session", {
          code: error.code,
          message: error.message,
        });

        if (error.code === "refresh_token_not_found" || error.message.includes("Invalid Refresh Token")) {
          await supabase.auth.signOut({ scope: "local" });
        }

        setSession(null);
        setLoading(false);
      } else {
        setSession(data.session ?? null);
        setLoading(false);
      }

      const authListener = supabase.auth.onAuthStateChange((_event, nextSession) => {
        if (!mounted) return;
        // Keep the session reference STABLE when nothing meaningful changed.
        // Supabase echoes auth events (INITIAL_SESSION / SIGNED_IN / token-refresh
        // sync) across same-origin contexts — e.g. the onboarding preview <iframe>
        // boots its own client on the shared auth storage. Setting a fresh session
        // object on each echo gave every consumer a new `user` reference, which
        // re-ran the dashboard data fetch and flashed the full-screen loader in a
        // loop (the "Tá pronto, chef" flicker). Only update on a real change.
        setSession((prev) => {
          if (
            prev?.access_token === nextSession?.access_token &&
            prev?.user?.id === nextSession?.user?.id
          ) {
            return prev;
          }
          return nextSession;
        });
        setLoading(false);
      });

      subscription = authListener.data.subscription;
    };

    void initializeAuth();

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
